import React, { useState, useEffect } from 'react'
import { Box, CircularProgress, Grid, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useIsOpenMenuLeftContext } from '../../common/utils/isOpenMenuLeftContext'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { IAnalyticsService, ANALYTICS_SERVICE_KEY } from '../../modules/forms'
import { getFormContainer } from '../../container/form-module'
import { FormStatByDate } from '../../modules/forms/api/AnalyticsApi'
import StatCard from './StatCard'
import FormStatModal from './FormStatModal'
import styles from './FormStatsChart.module.css'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const analyticsService = getFormContainer().get<IAnalyticsService>(ANALYTICS_SERVICE_KEY)

export type DateFilter = {
  startDate: Date
  finishDate: Date
}

export const OPTION_COLORS: Record<string, string> = {
  yes: '#ABD47B',
  no: '#EC6D62',
  veryGood: '#4CAF50',
  good: '#ABD47B',
  regular: '#F5C451',
  bad: '#EC6D62',
  veryBad: '#D32F2F',
}

const OPTION_ORDER = ['veryGood', 'good', 'regular', 'bad', 'veryBad', 'yes', 'no']

export const toISODate = (date: Date): string => {
  const d = new Date(date)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const lastWeekRange = (): DateFilter => ({
  startDate: new Date(new Date().setDate(new Date().getDate() - 6)),
  finishDate: new Date(),
})

export const toChartData = (
  stats: FormStatByDate[],
  optionLabels: Record<string, string>
): { labels: string[]; datasets: any[] } => {
  const countByOption: Record<string, number> = {}
  for (const s of stats) {
    countByOption[s.option] = (countByOption[s.option] ?? 0) + s.count
  }
  const options = OPTION_ORDER.filter((o) => o in countByOption)
  return {
    labels: options.map((o) => optionLabels[o] ?? o),
    datasets: [
      {
        label: '',
        data: options.map((o) => countByOption[o]),
        backgroundColor: options.map((o) => OPTION_COLORS[o] ?? 'var(--green-primary)'),
        borderRadius: 0,
      },
    ],
  }
}

const chartOptions = (yMax: number, showDetails: boolean) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: showDetails,
      position: 'top' as const,
      labels: {
        generateLabels: (chart: any) => {
          const dataset = chart.data.datasets[0]
          return chart.data.labels.map((label: string, i: number) => ({
            text: label,
            fillStyle: dataset.backgroundColor[i],
            strokeStyle: dataset.backgroundColor[i],
            hidden: false,
            index: i,
          }))
        },
      },
    },
    tooltip: {
      callbacks: { label: (ctx: any) => `  ${ctx.parsed.y} respuestas` },
    },
  },
  scales: {
    x: { display: showDetails, grid: { display: false } },
    y: {
      display: showDetails,
      beginAtZero: true,
      max: yMax,
      ticks: { stepSize: 1, precision: 0 },
    },
  },
})

export const ChartView = ({
  data,
  isLoading,
  showDetails,
}: {
  data: { labels: string[]; datasets: any[] }
  isLoading: boolean
  showDetails: boolean
}) => {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <Box className={styles.emptyState}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (data.labels.length === 0) {
    return (
      <Box className={styles.emptyState}>
        <Typography variant="body2" color="textSecondary">
          {t('noData')}
        </Typography>
      </Box>
    )
  }

  const maxValue = Math.max(0, ...data.datasets.flatMap((d: any) => d.data as number[]))
  const yMax = maxValue > 0 ? Math.ceil(maxValue * 1.35) : 5
  const styledDatasets = data.datasets.map((ds: any) => ({
    ...ds,
    barThickness: 30,
    borderRadius: 0,
  }))

  return (
    <Box className={showDetails ? styles.chartContainerDetailed : styles.chartContainer}>
      <Bar options={chartOptions(yMax, showDetails)} data={{ ...data, datasets: styledDatasets }} />
    </Box>
  )
}

const FormStatBox = ({
  title,
  userId,
  fetchStats,
  optionLabels,
}: {
  title: string
  userId: string
  fetchStats: (userId: string, start: string, end: string) => Promise<FormStatByDate[]>
  optionLabels: Record<string, string>
}) => {
  const [open, setOpen] = useState(false)
  const [chartData, setChartData] = useState<{ labels: string[]; datasets: any[] }>({
    labels: [],
    datasets: [],
  })
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const range = lastWeekRange()
    fetchStats(userId, toISODate(range.startDate), toISODate(range.finishDate))
      .then((s) => setChartData(toChartData(s, optionLabels)))
      .catch(() => setChartData({ labels: [], datasets: [] }))
      .finally(() => setIsLoading(false))
  }, [userId])

  return (
    <>
      <StatCard title={title} isLoading={isLoading} onViewHistory={() => setOpen(true)}>
        <ChartView data={chartData} isLoading={isLoading} showDetails={false} />
      </StatCard>
      <FormStatModal
        open={open}
        onClose={() => setOpen(false)}
        title={title}
        userId={userId}
        fetchStats={fetchStats}
        optionLabels={optionLabels}
      />
    </>
  )
}

const FormStatsChart = ({ userId }: { userId: string }) => {
  const { t } = useTranslation()
  const { openMenuLeft } = useIsOpenMenuLeftContext()

  const colSize = openMenuLeft ? 6 : 3

  const medicationLabels: Record<string, string> = { yes: t('yes'), no: t('no') }
  const qualityLabels: Record<string, string> = {
    veryGood: t('veryGood'),
    good: t('good'),
    regular: t('regular'),
    bad: t('bad'),
    veryBad: t('veryBad'),
  }

  const fetchMedication = (userId: string, start: string, end: string) =>
    analyticsService
      .getMedicationStatsByRange(userId, start, end)
      .toPromise()
      .then((r) => r || [])
  const fetchFood = (userId: string, start: string, end: string) =>
    analyticsService
      .getFoodStatsByRange(userId, start, end)
      .toPromise()
      .then((r) => r || [])
  const fetchSleep = (userId: string, start: string, end: string) =>
    analyticsService
      .getSleepStatsByRange(userId, start, end)
      .toPromise()
      .then((r) => r || [])
  const fetchDayRating = (userId: string, start: string, end: string) =>
    analyticsService
      .getDayRatingStatsByRange(userId, start, end)
      .toPromise()
      .then((r) => r || [])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={colSize} xl={3}>
        <FormStatBox
          title={t('medication')}
          userId={userId}
          fetchStats={fetchMedication}
          optionLabels={medicationLabels}
        />
      </Grid>
      <Grid item xs={12} sm={colSize} xl={3}>
        <FormStatBox
          title={t('food')}
          userId={userId}
          fetchStats={fetchFood}
          optionLabels={qualityLabels}
        />
      </Grid>
      <Grid item xs={12} sm={colSize} xl={3}>
        <FormStatBox
          title={t('sleep')}
          userId={userId}
          fetchStats={fetchSleep}
          optionLabels={qualityLabels}
        />
      </Grid>
      <Grid item xs={12} sm={colSize} xl={3}>
        <FormStatBox
          title={t('dayRating')}
          userId={userId}
          fetchStats={fetchDayRating}
          optionLabels={qualityLabels}
        />
      </Grid>
    </Grid>
  )
}

export default FormStatsChart
